import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { viewerMatches } from '@/components/layout/navigation';
import { useAuthStore } from '@/store/auth';
import { useOnboarding } from '@/hooks/useOnboarding';
import { usePermissions } from '@/hooks/usePermissions';
import { paths } from './paths';
import type { Permission } from '@/api/permissions';
import type { ModuleManifest } from '@/modules/manifest';
import { PageLoader } from '@/components/common/Loaders';

function Booting() {
  return (
    <PageLoader fullScreen />
  );
}

/** Blocks the app shell until the session is known, then sends anonymous users to sign in. */
export function RequireAuth() {
  const phase = useAuthStore((s) => s.phase);
  const location = useLocation();

  if (phase === 'booting') return <Booting />;
  if (phase === 'anonymous') {
    return (
      <Navigate to={paths.login} replace state={{ from: location.pathname + location.search }} />
    );
  }
  return <Outlet />;
}

/**
 * Holds an unverified organization inside the wizard.
 *
 * Permissions cannot answer this: a founder holds the full ORG_OWNER set from
 * the moment they register, so the shell asks `GET /onboarding` and branches on
 * `restricted` rather than probing an endpoint and reading a 403.
 */
export function RequireVerifiedOrganization() {
  const { restricted, isLoading } = useOnboarding();

  // Unknown is not "allowed": rendering the shell first would fire a dozen
  // requests that all come back ORGANIZATION_NOT_VERIFIED.
  if (restricted === undefined || isLoading) return <Booting />;
  if (restricted) return <Navigate to={paths.onboarding} replace />;
  return <Outlet />;
}

/** The wizard is pointless once the gate is open. */
export function RequireRestricted() {
  const { restricted, isLoading } = useOnboarding();
  if (restricted === undefined || isLoading) return <Booting />;
  if (!restricted) return <Navigate to={paths.modules} replace />;
  return <Outlet />;
}

/** Keeps a signed-in user out of the login/register screens. */
export function RequireAnonymous() {
  const phase = useAuthStore((s) => s.phase);
  if (phase === 'booting') return <Booting />;
  if (phase === 'authenticated') return <Navigate to={paths.modules} replace />;
  return <Outlet />;
}

/**
 * Route-level permission check. Deep links are the reason this exists: hiding a
 * nav item does not stop someone pasting the URL, and a bare 403 from the API
 * is a worse experience than an explicit page.
 */
export function RequirePermission({
  permission,
  mode = 'any',
}: {
  permission: Permission[];
  mode?: 'any' | 'all';
}) {
  const { can, canAny } = usePermissions();
  const allowed = mode === 'all' ? can(permission) : canAny(permission);
  return allowed ? <Outlet /> : <Navigate to={paths.forbidden} replace />;
}

/**
 * A module a viewer may not enter is refused, not merely undrawn.
 *
 * `RequirePermission` never looked at `isPlatform`, so an entry hidden by its
 * `audience` was hidden and nothing more: a platform admin holds the wildcard
 * and could open `/my-organization` by URL, on a page written for a caller who
 * has an organization. This calls the same `viewerMatches` the sidebar and the
 * apps grid call, and the route table wraps every module with it from the same
 * registry the sidebar is drawn from, so a module cannot be drawn and unguarded
 * or guarded and undrawn.
 *
 * It answers "may this person enter at all"; the per-screen `RequirePermission`
 * inside a module still answers "which screens" — the module gate is `anyOf` by
 * construction.
 */
export function RequireModule({ module }: { module: ModuleManifest }) {
  const { permissions, isPlatform } = usePermissions();
  return viewerMatches(module, { isPlatform, permissions }) ? (
    <Outlet />
  ) : (
    <Navigate to={paths.forbidden} replace />
  );
}
